import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DataGrid } from "@mui/x-data-grid";
import { AiOutlineDelete } from "react-icons/ai";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
} from "@mui/material";
import styles from "../../styles/styles";
import { RxCross1 } from "react-icons/rx";
import axios from "axios";
import { server } from "../../server";
import { toast } from "react-toastify";
import { getAllSellers } from "../../redux/actions/sellers";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx"; 
import { saveAs } from "file-saver";
import Loader from "../Layout/Loader"; 

const AllSellers = () => {
  const dispatch = useDispatch();
  const { sellers = [] } = useSelector((state) => state.seller);
  const [open, setOpen] = useState(false);
  const [userId, setUserId] = useState("");
  const [uploadFiles, setUploadFiles] = useState({});
  const [uploadPopupOpen, setUploadPopupOpen] = useState(false);
  const [selectedSellerId, setSelectedSellerId] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [bulkActionType, setBulkActionType] = useState(null);
  const [isBulkEdit, setIsBulkEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const fileInputsRef = useRef({});

  // Filter and Sort persistence
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [sortModel, setSortModel] = useState([]);

  useEffect(() => {
    // Load saved filters only if navigating back (not on fresh page load)
    const navigationEntry = performance.getEntriesByType('navigation')[0];
    const isPageReload = navigationEntry && navigationEntry.type === 'reload';
    
    if (!isPageReload) {
      const savedFilter = sessionStorage.getItem('allSellersFilterModel');
      const savedSort = sessionStorage.getItem('allSellersSortModel');
      
      if (savedFilter) {
        setFilterModel(JSON.parse(savedFilter));
      }
      if (savedSort) {
        setSortModel(JSON.parse(savedSort));
      }
    } else {
      // Clear filters on page reload
      sessionStorage.removeItem('allSellersFilterModel');
      sessionStorage.removeItem('allSellersSortModel');
    }
  }, []);

  useEffect(() => {
    const fetchAllSellers = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${server}/shop/admin-all-sellers`, {
          withCredentials: true,
        });
        dispatch(getAllSellers(response.data.sellers));
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || "Failed to fetch sellers";
        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAllSellers();
  }, [dispatch]);

  const handleDownloadAllProductsHybrid = async (sellerId) => {
    if (!sellerId) {
      toast.error("Seller ID is missing.");
      return;
    }

    try {
      const response = await axios.get(
        `${server}/product/get-all-products-data/${sellerId}`,
        {
          withCredentials: true,
        }
      );

      if (response.data.success && response.data.products.length > 0) {
        const productsData = response.data.products;
        const maxSlabs = Math.max(
          ...productsData.map((product) =>
            product.quantitySlabs ? product.quantitySlabs.length : 0
          )
        );

        const baseColumns = [
          "ProductId",
          "Name",
          "Description",
          "Category",
          "Tags",
          "PricePerQty",
          "GSTPercentage",
          "CommissionRate",
          "HSNCode",
          "LeadTime",
          "Stock",
          "StockAvailability",
          "HasVariants",
          "VariantTypes",
          "VariantItems",
          "VariantStock",
          "Images",
          "Shop",
          "SoldOut",
          "CreatedAt",
          "MinimumOrderQuantity",
          "ServiceableLocation",
        ];

        const quantitySlabHeaders = [];
        for (let i = 1; i <= maxSlabs; i++) {
          quantitySlabHeaders.push(
            `QuantitySlab${i}_Min`,
            `QuantitySlab${i}_Max`,
            `QuantitySlab${i}_OriginalPrice`,
            `QuantitySlab${i}_DiscountPrice`
          );
        }

        const allColumns = [...baseColumns, ...quantitySlabHeaders];

        const dataToExport = productsData.map((product) => {
          const rowData = {
            ProductId: product._id ? product._id.toString() : "",
            Name: product.name,
            Description: product.description,
            Category: product.category,
            Tags: product.tags,
            PricePerQty: product.pricePerQty,
            GSTPercentage: product.gstPercentage,
            CommissionRate: product.commissionRate,
            HSNCode: product.hsnCode,
            LeadTime: product.leadTime,
            Stock: product.stock,
            StockAvailability: product.stockAvailability,
            HasVariants: product.hasVariants ? "TRUE" : "FALSE",
            VariantTypes: product.variantTypes
              ? JSON.stringify(product.variantTypes)
              : "",
            VariantItems: product.variantItems
              ? JSON.stringify(product.variantItems)
              : "",
            VariantStock: product.variantStock
              ? JSON.stringify(product.variantStock)
              : "",
            Images: (product.images || [])
              .map((img) => img.url)
              .join(", "),
            Shop: product.shop ? product.shop.toString() : "",
            SoldOut: product.sold_out,
            CreatedAt: product.createdAt
              ? new Date(product.createdAt).toLocaleDateString()
              : "",
            MinimumOrderQuantity: product.minimumOrderQuantity ,
            ServiceableLocation: product.serviceableLocation,
          };

          if (product.quantitySlabs && product.quantitySlabs.length > 0) {
            product.quantitySlabs.forEach((slab, index) => {
              const slabNum = index + 1;
              rowData[`QuantitySlab${slabNum}_Min`] = slab.min;
              rowData[`QuantitySlab${slabNum}_Max`] = slab.max;
              rowData[`QuantitySlab${slabNum}_OriginalPrice`] = slab.originalPrice;
              rowData[`QuantitySlab${slabNum}_DiscountPrice`] = slab.discountPrice;
            });

            for (let i = product.quantitySlabs.length + 1; i <= maxSlabs; i++) {
              rowData[`QuantitySlab${i}_Min`] = "";
              rowData[`QuantitySlab${i}_Max`] = "";
              rowData[`QuantitySlab${i}_OriginalPrice`] = "";
              rowData[`QuantitySlab${i}_DiscountPrice`] = "";
            }
          } else {
            for (let i = 1; i <= maxSlabs; i++) {
              rowData[`QuantitySlab${i}_Min`] = "";
              rowData[`QuantitySlab${i}_Max`] = "";
              rowData[`QuantitySlab${i}_OriginalPrice`] = "";
              rowData[`QuantitySlab${i}_DiscountPrice`] = "";
            }
          }

          return rowData;
        });

        const worksheet = XLSX.utils.json_to_sheet(dataToExport, {
          header: allColumns,
        });
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Products");
        const excelBuffer = XLSX.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });
        const blob = new Blob([excelBuffer], {
          type:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const fileName = `Products_${sellerId}_${new Date()
          .toISOString()
          .slice(0, 10)}.xlsx`;
        saveAs(blob, fileName);
        toast.success("Products downloaded successfully!");
      } else {
        toast.error("No products available to download.");
      }
    } catch (error) {
      console.error("Error downloading products:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to download products.";
      toast.error(errorMessage);
    }
  };

  const resetFileInput = (sellerId) => {
    const key = isBulkEdit ? `edit-${sellerId}` : `upload-${sellerId}`;
    if (fileInputsRef.current[key]) {
      fileInputsRef.current[key].value = null;
    }
  };

  const handleCloseUploadPopup = () => {
    setUploadPopupOpen(false);
    resetFileInput(selectedSellerId);
    setSelectedSellerId(null);
    setIsBulkEdit(false);
    setBulkActionType(null);
  };

  const handleBulkAction = async () => {
    const sellerId = selectedSellerId;
    const file = uploadFiles[sellerId];
    if (!file) {
      toast.error("Please select a file first!");
      return;
    }
    setIsUploading(true);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const url =
        isBulkEdit
          ? `${server}/product/bulk-edit/${sellerId}`
          : `${server}/product/bulk-upload/${sellerId}`;

      const response = await axios.post(url, formData, {
        withCredentials: true,
      });

      setIsUploading(false);
      handleCloseUploadPopup();

      toast.success(response.data.message);
      dispatch(getAllSellers());
    } catch (error) {
      setIsUploading(false);
      handleCloseUploadPopup();
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to process bulk action";
      toast.error(errorMessage);
    } finally {
      setUploadFiles((prev) => {
        const newUploadFiles = { ...prev };
        delete newUploadFiles[sellerId];
        return newUploadFiles;
      });
      resetFileInput(sellerId);
    }
  };

  const handleDeleteSeller = async (id) => {
    try {
      const response = await axios.delete(`${server}/shop/delete-seller/${id}`, {
        withCredentials: true,
      });
      toast.success(response.data.message);
      dispatch(getAllSellers());
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to delete seller.";
      toast.error(errorMessage);
    }
  };

  // Updated column definitions with fixed widths
  const columns = [
    { field: "id", headerName: "Seller ID", width: 150 },
    { field: "name", headerName: "Name", width: 130 },
    { field: "email", headerName: "Email", width: 130 },
    { field: "address", headerName: "Seller Address", width: 130 },
    
    {
      field: "balance",
      headerName: "Balance",
      width: 120,
      renderCell: (params) => (
        <span>
          {typeof params.row.balance === "number"
            ? `Rs ${params.row.balance.toFixed(2)}`
            : "N/A"}
        </span>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      renderCell: (params) => (
        <Link
          to={`/admin/seller-transactions/${params.id}`}
          style={{ color: "#007bff", textDecoration: "none" }}
        >
          View Transactions
        </Link>
      ),
    },
    {
      field: "viewProducts",
      headerName: "View Products",
      width: 150,
      renderCell: (params) => (
        <Link
          to={`/admin/seller-products/${params.id}`}
          style={{ color: "#007bff", textDecoration: "none" }}
        >
          View Products
        </Link>
      ),
    },
    {
      field: "downloadProducts",
      headerName: "Download Products",
      width: 200,
      renderCell: (params) => (
        <Button
          onClick={() => handleDownloadAllProductsHybrid(params.id)}
          variant="contained"
          color="primary"
        >
          Download Products
        </Button>
      ),
    },
    {
      field: "bulkUpload",
      headerName: "Bulk Upload",
      width: 130,
      renderCell: (params) => {
        const sellerId = params.id;
        return (
          <>
            <input
              type="file"
              accept=".xlsx"
              onChange={(e) => {
                setUploadFiles((prev) => ({
                  ...prev,
                  [sellerId]: e.target.files[0],
                }));
                setSelectedSellerId(sellerId);
                setIsBulkEdit(false);
                setBulkActionType("upload");
                setUploadPopupOpen(true);
              }}
              style={{ display: "none" }}
              id={`bulk-upload-input-${sellerId}`}
              ref={(input) =>
                (fileInputsRef.current[`upload-${sellerId}`] = input)
              }
            />
            <Button  
              variant="contained"
              component="span"
              onClick={() =>
                document
                  .getElementById(`bulk-upload-input-${sellerId}`)
                  .click()
              }
              color="secondary"
            >
              Bulk Upload
            </Button>
          </>
        );
      },
    },
    {
      field: "bulkEdit",
      headerName: "Bulk Edit",
      width: 120,
      renderCell: (params) => {
        const sellerId = params.id;
        return (
          <>
            <input
              type="file"
              accept=".xlsx"
              onChange={(e) => {
                setUploadFiles((prev) => ({
                  ...prev,
                  [sellerId]: e.target.files[0],
                }));
                setSelectedSellerId(sellerId);
                setIsBulkEdit(true);
                setBulkActionType("edit");
                setUploadPopupOpen(true);
              }}
              style={{ display: "none" }}
              id={`bulk-edit-input-${sellerId}`}
              ref={(input) =>
                (fileInputsRef.current[`edit-${sellerId}`] = input)
              }
            />
            <Button
              variant="contained"
              component="span"
              onClick={() =>
                document
                  .getElementById(`bulk-edit-input-${sellerId}`)
                  .click()
              }
              color="secondary"
            >
              Bulk Edit
            </Button>
          </>
        );
      },
    },
    {
      field: "deleteSeller",
      headerName: "Delete Seller",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          onClick={() => {
            setUserId(params.id);
            setOpen(true);
          }}
          color="error"
        >
          <AiOutlineDelete size={20} />
        </Button>
      ),
    },
  ];

  const rows = sellers.map((item) => ({
    id: item._id,
    name: item.name,
    email: item.email,
    address: item.address,
    balance: item.balance,
  }));

  return (
    <div className="w-full">
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <h3 className="text-[22px] font-Poppins pb-2">All Sellers</h3>
          <div className="w-full min-h-[45vh] bg-white rounded">
        <div className="datagrid-scroll-wrapper">
          <div className="table-inner-container" style={{ minWidth: "1540px" }}>
            <DataGrid
              rows={rows}
              columns={columns}
              pageSize={10}
              disableSelectionOnClick
              autoHeight
              filterModel={filterModel}
              onFilterModelChange={(newFilterModel) => {
                setFilterModel(newFilterModel);
                sessionStorage.setItem('allSellersFilterModel', JSON.stringify(newFilterModel));
              }}
              sortModel={sortModel}
              onSortModelChange={(newSortModel) => {
                setSortModel(newSortModel);
                sessionStorage.setItem('allSellersSortModel', JSON.stringify(newSortModel));
              }}
              sx={{
                fontSize: "0.8rem",
                border: "none",
                "& .MuiDataGrid-cell": { fontSize: "0.8rem", padding: "0.5rem" },
                "& .MuiDataGrid-columnHeader": {
                  fontSize: "0.8rem",
                  padding: "0.5rem",
                },
                "& .MuiDataGrid-virtualScroller": {
                  overflow: "visible !important",
                },
              }}
            />
          </div>
        </div>
      </div>

      {open && (
        <div className="w-full fixed top-0 left-0 z-[999] bg-[#00000039] flex items-center justify-center h-screen">
          <div className="w-[95%] 800px:w-[40%] min-h-[20vh] bg-white rounded shadow p-5">
            <div className="w-full flex justify-end cursor-pointer">
              <RxCross1 size={25} onClick={() => setOpen(false)} />
            </div>
            <h3 className="text-[25px] text-center py-5 font-Poppins text-[#000000cb]">
              Are you sure you want to delete this user?
            </h3>
            <div className="w-full flex items-center justify-center">
              <div
                className={`${styles.button} text-white text-[18px] !h-[42px] mr-4`}
                onClick={() => setOpen(false)}
              >
                Cancel
              </div>
              <div
                className={`${styles.button} text-white text-[18px] !h-[42px] ml-4`}
                onClick={() => {
                  setOpen(false);
                  handleDeleteSeller(userId);
                }}
              >
                Confirm
              </div>
            </div>
          </div>
        </div>
      )}

      <Dialog
        open={uploadPopupOpen}
        onClose={handleCloseUploadPopup}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {bulkActionType === "edit" ? "Bulk Edit Products" : "Bulk Upload Products"}
        </DialogTitle>
        <DialogContent>
          {isUploading ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "20px 0",
              }}
            >
              <CircularProgress />
              <p style={{ marginTop: "20px", textAlign: "center" }}>
                {bulkActionType === "edit"
                  ? "Editing products. Please do not reload or refresh."
                  : "Uploading products. Please do not reload or refresh."}
              </p>
            </div>
          ) : (
            <p>
              Are you sure you want to {bulkActionType === "edit" ? "edit" : "upload"} this
              file?
            </p>
          )}
        </DialogContent>
        <DialogActions>
          {!isUploading && (
            <>
              <Button onClick={handleCloseUploadPopup} color="secondary">
                Cancel
              </Button>
              <Button onClick={handleBulkAction} color="primary">
                {bulkActionType === "edit" ? "Edit" : "Update"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
        </>
      )}
    </div>
  );
};

export default AllSellers;
